// DATE-PICKER-STANDARDIZE — the shared date/month control used throughout
// the app. The native input is intentionally the visible, focusable control:
// it owns the browser picker and keyboard editing instead of sitting behind
// an opacity-zero overlay that cancels mouse events.
//
// Usage:
//   <DateInput
//     value={someIsoDate}   // "YYYY-MM-DD" ("" for empty)
//     onChange={(e) => handleChange(e.target.value)}
//     disabled={busy}
//     aria-label="Target action date"
//   />
//
// Pass type="month" for month pickers (value format "YYYY-MM").
// forwardRef points at the actual visible input.
//
// NOTE for the enforcement gate (src/__tests__/dateInputEnforcement.test.js):
// this is the ONLY file in the app allowed to contain a raw
// <input type="date">/<input type="month">. Every other consumer must
// render <DateInput> instead.

import React, { useRef } from "react";
import { Calendar } from "lucide-react";
import { T } from "../app/tokens.js";
import "./DateInput.css";

export const DateInput = React.forwardRef(function DateInput(
  { value, onChange, disabled, style = {}, type = "date", placeholder = "Set a date", className = "", ...rest },
  ref,
) {
  function setInputRef(node) {
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }

  // Keep the distinction explicit for month inputs: the browser validates and
  // displays YYYY-MM while date inputs validate and display YYYY-MM-DD.
  const inputMinWidth = type === "month" ? 120 : 110;
  const inputStyle = {
    boxSizing: "border-box",
    display: "block",
    width: "100%",
    minWidth: inputMinWidth,
    fontFamily: "inherit",
    fontSize: T.fsSm5,
    padding: "4px 30px 4px 8px",
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    background: T.white,
    color: T.dark,
    cursor: disabled ? "wait" : "text",
    ...style,
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div data-testid="date-input-box" style={{ position: "relative" }}>
        <input
          ref={setInputRef}
          type={type}
          value={value ?? ""}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onChange}
          className={`date-input-native${className ? ` ${className}` : ""}`}
          {...rest}
          style={inputStyle}
        />
        <Calendar
          aria-hidden="true"
          size={14}
          color={T.light}
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
});